import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, X } from 'lucide-react';

/**
 * Location Autocomplete Component using OpenStreetMap Nominatim API
 * Provides address search with suggestions for US addresses
 */
const LocationAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Search address...",
  label = "Location",
  required = false,
  className = "",
  type = "full" // 'full' | 'city' | 'zip'
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);
  const debounceTimer = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format display value from address object
  useEffect(() => {
    if (value && typeof value === 'object') {
      const parts = [];
      if (value.street) parts.push(value.street);
      if (value.city) parts.push(value.city);
      if (value.state) parts.push(value.state);
      if (value.zipCode) parts.push(value.zipCode);
      setQuery(parts.join(', '));
    } else if (typeof value === 'string') {
      setQuery(value);
    }
  }, [value]);

  // Search OSM Nominatim API
  const searchAddress = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Add USA bias and address details
      const params = new URLSearchParams({
        q: searchQuery,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        countrycodes: 'us', // Limit to USA
        'accept-language': 'en'
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            'User-Agent': 'SymptoConnect/1.0 (healthcare platform)'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch address suggestions');
      }

      const data = await response.json();
      
      // Format suggestions for US addresses
      const formatted = data.map(item => {
        const addr = item.address || {};
        
        // Extract street number and name
        const streetNum = addr.house_number || '';
        const streetName = addr.road || addr.pedestrian || addr.street || '';
        const street = streetNum && streetName 
          ? `${streetNum} ${streetName}`
          : streetName || streetNum || item.display_name.split(',')[0];

        return {
          id: item.place_id,
          displayName: item.display_name,
          street: street,
          city: addr.city || addr.town || addr.village || addr.hamlet || '',
          state: addr.state || addr.province || '',
          zipCode: addr.postcode || '',
          country: addr.country || 'USA',
          lat: item.lat,
          lon: item.lon,
          type: item.type
        };
      }).filter(item => item.city && item.state); // Only show valid US addresses

      setSuggestions(formatted);
      setIsOpen(formatted.length > 0);
    } catch (err) {
      console.error('OSM search error:', err);
      setError('Failed to load address suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      searchAddress(newQuery);
    }, 300);
  };

  // Select address
  const handleSelect = (suggestion) => {
    const addressData = {
      street: suggestion.street || '',
      city: suggestion.city,
      state: suggestion.state,
      zipCode: suggestion.zipCode,
      country: suggestion.country,
      lat: suggestion.lat,
      lon: suggestion.lon
    };

    onChange(addressData);
    setQuery(suggestion.displayName);
    setIsOpen(false);
    setSuggestions([]);
  };

  // Clear selection
  const handleClear = () => {
    setQuery('');
    onChange({ street: '', city: '', state: '', zipCode: '', country: 'USA' });
    setSuggestions([]);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          <MapPin className="w-5 h-5" />
        </div>
        
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 3 && suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2.5 border border-slate-300 rounded-lg 
                     focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                     text-sm text-slate-900 placeholder-slate-400"
        />
        
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 
                       hover:text-slate-600 transition-colors"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {loading && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          <ul className="py-1">
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.id}
                onClick={() => handleSelect(suggestion)}
                className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {suggestion.street || suggestion.city}
                    </p>
                    <p className="text-xs text-slate-500">
                      {suggestion.city}, {suggestion.state} {suggestion.zipCode}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          
          {/* OSM Attribution */}
          <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200">
            <p className="text-xs text-slate-400">
              © OpenStreetMap contributors
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}

      {/* Manual Entry Helper */}
      <p className="mt-1.5 text-xs text-slate-500">
        Start typing to search US addresses, or enter manually
      </p>
    </div>
  );
};

export default LocationAutocomplete;
